/**
 * `/student/courses` — read-only list of the student's enrolled mock
 * courses with a per-course progress chip. No real LMS data, no real
 * grades.
 */

import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/States';

export function StudentCourses() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';
  const state = useFetch(
    () => client.listStudentCourses(token),
    [client, token],
  );

  if (state.status === 'loading') {
    return <LoadingState label="Loading your courses…" />;
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} />;
  }
  if (state.data.length === 0) {
    return (
      <EmptyState
        title="You are not enrolled in any courses yet"
        description="Course rosters are mock-only in this MVP. Ask your teacher to seed you into a sample course."
      />
    );
  }

  return (
    <section className="page" data-testid="student-courses">
      <header className="page__header">
        <h1>Courses</h1>
        <p className="muted">Mock course list. No real LMS data is loaded.</p>
      </header>

      <table className="table" aria-label="Enrolled courses">
        <thead>
          <tr>
            <th scope="col">Course</th>
            <th scope="col">Code</th>
            <th scope="col">Term</th>
            <th scope="col">Progress</th>
            <th scope="col">Open assignments</th>
          </tr>
        </thead>
        <tbody>
          {state.data.map((course) => (
            <tr key={course.id} data-testid="student-course-row">
              <td>{course.name}</td>
              <td>
                <code>{course.code}</code>
              </td>
              <td>{course.termName}</td>
              <td>
                <ProgressBar percent={course.progressPercent} />
              </td>
              <td>{course.openAssignments}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${clamped}% complete`}
    >
      <div className="progress__bar" style={{ width: `${clamped}%` }} />
      <span className="progress__label">{clamped}%</span>
    </div>
  );
}
